import { CartesianCoordinate, CylindricalCoordinate } from "./Coordinate";
import { Plate } from "./Plate";
import {
  CaptureProfile,
  ELightPath,
  PsrCaptureProfile,
} from "./CaptureProfile";
import {
  ETestingType,
  ImagingTestParameter,
  ObjectiveSwitchingTestParameter,
  LightPathSwitchingTestParameter,
  PlateBumpTestParameter,
} from "./TestingParameter";
import { EImagingMode } from "./EImagingMode";
import {
  MovementTestParameter,
  ReliabilityTestParameter,
  BarcodeScanTestParameter,
} from "./TestingParameter";
import ExtendedCylindricalDefaultVelocity from "../components/Settings/VectorEditor/DefaultVelocity";
import { ECarrier } from "./ECarrier";
import { XSafePositionCoordinate } from "./XSafePositionCooridnate";

export enum EImagingModuleStatus {
  Unknown = 1,
  Faulted = 2,
  Homing = 3,
  Idle = 4,
  Imageable = 5,
  Imaging = 6,
  Starting = 7,
  Stopped = 8,
  Stopping = 9,
  Uninitialized = 10,
  Ejected = 11,
  Ejecting = 12,
  Retracting = 13,
  Diagnosing = 14,
  Testing = 15,
  Busy = 16,
  Crash = 17,
  Cancelling = 18,
}

export interface LightPathOpticMotorPosition {
  objectiveZoom?: number;
  filterCubes?: number;
}

export interface OpticMotorPosition {
  visible: LightPathOpticMotorPosition;
  fluorescence: LightPathOpticMotorPosition;
  ultraviolet: LightPathOpticMotorPosition;
}

export enum ESpeed {
  Unknown = 0,
  Slow = 1,
  SlowMedium = 2,
  Medium = 3,
  MediumFast = 4,
  Fast = 5,
}

export interface LevellingState {
  lowBound: number;
  highBound: number;
  autoLevel: boolean;
}

export interface ImagerState {
  isPlateBumpActivated: boolean | null;
  imageWidth: number;
  imageHeight: number;
  fovX: number;
  fovY: number;
  imagingArmPosition: CartesianCoordinate;
  cameraPositionRelativeToWell: CartesianCoordinate;
  defaultVelocity: CartesianCoordinate;
  freerunCapture: boolean;
  plate?: Plate;
  activeLightPath: ELightPath;
  captureProfile: CaptureProfile;
  wellNumber: number;
  dropNumber: number;
  wellName: string;
  opticMotorPosition: OpticMotorPosition;
  streamServerPort: number;
  state: EImagingModuleStatus;
  status: string;
  testing: ImagingModuleTestingState;
  bestFit: boolean;
  isOverviewing: boolean;
  levelling: LevellingState;
  isImagingArmMoving: boolean;
  resourceStatus: ResourceStatus;
  maxPosition: CartesianCoordinate;
  minPosition: CartesianCoordinate;
}

export enum ESuiteMode {
  Normal = 0,
  Testing = 1,
  AbandonedPlate = 2,
  Error = 3,
  ArmCrashed = 4,
  Homing = 5,
  Diagnosing = 6,
  Initializing = 7,
  AutoCalibrating = 8,
  ReturningAbandonedPlate = 9,
  SlotCorrection = 10,
  ReconnectingHardware = 11,
  FindingSafePosition = 12,
}

export interface SuiteState {
  suiteMode: ESuiteMode;
  imagingMode: EImagingMode;
  status: string;
  testing: SuiteModuleTestingState;
  abandonedPlate: SuiteModuleAbandonedPlateState;
  crashed: SuiteModuleCrashedState;
  faulted: SuiteModuleFaultedState;
  initializing: SuiteModuleInitializingState;
  autoCalibrating: SuiteModuleAutoCalibratingState;
  limsName: string;
  instrumentName: string;
  instrumentType: EInstrumentType;
  instrumentTypeName: string;
  applicationUpdate: ApplicationUpdateStatus;
  slotCorrection: SuiteModuleSlotCorrectionState;
  limsConnectionStatus: LimsConnectionStatus;
  findingXSafePosition: SuiteModuleAutoCalibratingState;
  alwaysShowSettingsAndTools: boolean;
  isHardwareConnected: boolean;
}

export enum EInstrumentType {
  Unknown = 0,
  RI360 = 7,
  RI1000 = 4,
}

export enum ESuiteModeState {
  Starting,
  Started,
  Stopping,
  Stopped,
}
export interface SuiteModuleAutoCalibratingState {
  modeState: ESuiteModeState;
}
export interface SuiteModuleSlotCorrectionState {
  modeState: ESuiteModeState;
  status: string;
  isSlotCorrectionSuccess: boolean;
  isUpdatedSlotCorrectionExist: boolean;
}

export interface SuiteModuleInitializingState {
  messages: string[];
}

export interface SuiteModuleAbandonedPlateState {
  message: string;
  instruction: string;
}

export interface SuiteModuleCrashedState {
  message: string;
  instruction: string;
}

export interface LimsConnectionStatus {
  status: ELimsConnectionStatus;
}

export enum EFaultedReason {
  NoError = 0,
  UnhandledException = 1,
  DiagnosticFailed = 2,
  DiagnosticFailedAcknowledged = 3,
  MotorCrashed = 4,
  PlateInspectionError = 5,
  ReturnPlateFailed = 6,
  MultiplePlateRetrievalFailure = 7,
  MultiplePlateRetrievalFailureAcknowledged = 8,
}

export interface SuiteModuleFaultedState {
  title: string;
  message: string;
  reason: EFaultedReason;
  detail: any;
}

export interface ChamberState {
  fullCapacity: number;
  remainingCapacity: number;
  interiorLight?: boolean;
  isMainDoorOpen?: boolean;
  enableAlmostFullCapacityWarning?: boolean;
  almostFullCapacityThresholdInPercent?: number;
  temperatureControlType?: number;
}

export interface LoadPortState {
  fullCapacity: number;
  remainingCapacity: number;
  isLoadPortDoorOpen?: boolean;
  loadPortScannedStatus: ELoadPortScannedStatus;
}

export enum EPsrState {
  Unknown = 0,
  Busy = 1,
  Available = 2,
  Fail = 3,
}

export enum EPsrRetryState {
  None = 0, RetryStore = 1, RetryRetrieve = 2
}

export enum EPsrPosition {
  Unknown,
  SafePosition,
  FrontOfFixedCarriage,
  FrontOfMovableCarrier1,
  FrontOfMovableCarrier2,
  FrontOfImager,
  FrontOfLoadPort,
}

export enum ELimsConnectionStatus {
  Unknown = 0,
  Connected = 1,
  Disconnected = 2,
  Faulted = -1,
}

export enum ELoadPortScannedStatus {
  Unknown = 0,
  Scanned = 1,
  Unscanned = 2,
}

export interface CarrierState {
  carrier: ECarrier;
  autoCalibrationEnabled: boolean;
}

export interface PsrCameraState {
  psrCameraId: number;
  imageWidth: number;
  imageHeight: number;
  fovX: number;
  fovY: number;
  isStreaming: boolean;
  isDetectingFiducialMarker: boolean;
  isDetectingBarcode: boolean;
  streamServerPort: number;
  captureProfile: PsrCaptureProfile;
}

export interface PsrState {
  isElectromagnetEngaged: boolean | null;
  isPickUpMotorExtended: boolean | null;
  status: string;
  state: EPsrState;
  isPsrMoving: boolean;
  plateFootprintStatus: string;
  cartesianPosition: CartesianCoordinate;
  cartesianDefaultVelocity: CartesianCoordinate;
  barcodeScanner: BarcodeScannerState;
  cylindricalPosition: CylindricalCoordinate;
  cylindricalDefaultVelocity: CylindricalCoordinate;
  carrierStates: CarrierState[];
  psrCameras: PsrCameraState[];
  platePresenceSensor: boolean;
  location: EPsrPosition;
  xSafePositionCoordinate: XSafePositionCoordinate;
  retryState: EPsrRetryState;
}

export interface BarcodeScannerState {
  isScanning: boolean;
}

export enum EImageProcessorState {
  Unknown = 0,
  Idle = 1,
  Busy = 2,
  Stopped = 3,
  Started = 4,
  Stopping = 5,
  Starting = 6,
  Off = 7,
}
export enum EChillerState {
  Unknown = 0,
  Ready = 1,
  Warning = 2,
  Error = 3,
}
export enum EChillerRecoveryStatus {
  Ready = 0,
  Recovery = 1,
}
export enum EChillerControlStatus {
  Unknown = 0,
  Connected = 1,
  Disconnected = 2,
  Disabled = 3,
}

export interface ChillerControlState {
  name: string;
  connectionStatus: EChillerControlStatus;
  sensorErrorList: string[];
  isInitialConnectionSuccess: boolean;
  recoveryStatus: number;
}

export interface ChillerState {
  totalChiller: number;
  chillerControlState: ChillerControlState[];
  output: number;
  state: EChillerState;
}

export interface ImageProcessorState {
  state: EImageProcessorState;
  numberOfProcessingThread: number;
  imageProccessorThreadStates: EImageProcessorState[];
  unprocessedImageCount: number;
  displayUnprocessedImagesCount: boolean;
  displayImages: boolean;
  resourceStatus: ResourceStatus;
  machineName: string;
  limsConnectionStatus: LimsConnectionStatus;
}

export interface FisState {
  state: number;
  limsName: ELimsConnectionStatus;
}

export interface ModuleState {
  name: string;
  identifier: string;
  moduleType: number;
  imager?: ImagerState;
  suite?: SuiteState;
  chamber?: ChamberState;
  loadPort?: LoadPortState;
  psr?: PsrState;
  imageProcessor?: ImageProcessorState;
  chiller?: ChillerState;
  fis?: FisState;
}

export interface Fov {
  width: number;
  height: number;
}

export enum ETestingState {
  Starting,
  Started,
  Stopping,
  Stopped,
}

export interface TestingStateBase {
  type: ETestingType;
  state: ETestingState;
  counter: number;
}

export interface ImagingTestingState extends TestingStateBase {
  parameter?: ImagingTestParameter;
}

export interface ReliabilityTestingState extends TestingStateBase {
  parameter?: ReliabilityTestParameter;
}

export interface AutoExposureTestingState extends TestingStateBase { }

export interface ObjectiveSwitchingTestingState extends TestingStateBase {
  parameter?: ObjectiveSwitchingTestParameter;
}

export interface LightPathSwitchingTestingState extends TestingStateBase {
  parameter?: LightPathSwitchingTestParameter;
}

export interface PlateBumpTestingState extends TestingStateBase {
  parameter?: PlateBumpTestParameter;
}

export interface CondenserTestingState extends TestingStateBase { }

export interface ImagingModuleTestingState {
  autoExposure?: AutoExposureTestingState;
  objectiveSwitching?: ObjectiveSwitchingTestingState;
  lightPathSwitching?: LightPathSwitchingTestingState;
  condenser?: CondenserTestingState;
}

export interface SuiteModuleTestingState {
  movement?: MovementTestingState;
  plateBump?: PlateBumpTestingState;
  imaging?: ImagingTestingState;
  reliability?: ReliabilityTestingState;
  barcodeScan?: BarcodeScanTestingState;
}

export enum ETestingTaskState {
  Imaging = 0,
  PlateBump,
  LightpathSwitching,
  ObjectiveSwitching,
  Condenser,
  AutoExposure,
  Reliability,
  Barcode,
  Movement,
}

export interface MovementTestingState extends TestingStateBase {
  status: string;
  counterPsrArm: number;
  counterImagerArm: number;
  counterCarrier: number;
  counterMfiFilterCubes: number;
  parameter?: MovementTestParameter;
}

export interface BarcodeScanTestingState extends TestingStateBase {
  parameter?: BarcodeScanTestParameter;
  status: string;
}

export interface ResourceStatus {
  memory: MemoryResourceStatusDto;
  driveSpace: DriveResourceStatusDto;
  lims: LimsResourceStatusDto;
}

export interface MemoryResourceStatusDto {
  requiredAdditionalFreeMemoryInByte: number;
  availableFreeMemoryInByte: number;
  requiredFreeMemoryInByte: number;
  isAvailable: boolean;
  name: string;
  resourceType: EResourceType;
  totalMemoryInByte: number;
  memoryInUseInByte: number;
  memoryUsedByCurrentProcess: number;
}

export interface DriveResourceStatusDto {
  requiredAdditionalFreeSpaceInByte: number;
  availableFreeSpaceInByte: number;
  requiredFreeSpaceInByte: number;
  isAvailable: boolean;
  name: string;
  resourceType: EResourceType;
  totalDriveSpaceInByte: number;
}

export interface LimsResourceStatusDto {
  isAvailable: boolean;
  name: string;
  resourceType: EResourceType;
}

enum EResourceType {
  DriveSpace,
  Memory,
  Lims,
}

export interface ApplicationUpdateStatus {
  isApplicationUpdateAvailable: boolean;
  isAutomaticCheckUpdate: boolean;
  releaseBuildDate: string;
  releaseNotesUrl: string;
  releasePathUrl: string;
  releaseType: EReleaseType;
  releaseVersion: string;
}

enum EReleaseType {
  Undefined = 0,
  Beta = 1,
  GeneralAvailable = 2,
  ReleaseCandidate = 3,
  Trial = 4,
}

export const ARM_CARRIER_WAYPOINT_SELECTION = "ARM_CARRIER_WAYPOINT_SELECTION"